import { Case, getCollectorOnCase } from '@debbie/common/dist/cases/index.js'
import { PaymentOption } from '@debbie/common/dist/collectors/index.js'
import * as _collectors from '@debbie/common/dist/collectors/index.js'
import { CaseVoucher } from '@debbie/common/dist/economy/case-vouchers/index.js'
import * as _paymentPlans from '@debbie/common/dist/economy/payment-plans/index.js'
import { formatNumber, validatePhone } from '@debbie/common/dist/index.js'
import { User } from '@debbie/common/dist/users/index.js'
import { useCollector } from '@debbie/cortex/dist/collectors/index.js'
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js'
import { h } from '@debbie/cortex/dist/index.js'
import { usePaymentPlansByCaseId } from '@debbie/cortex/dist/paymentPlans/index.js'
import { RootState } from '@debbie/cortex/dist/reducer.js'
import ArrowRight from '@debbie/iris/dist/components/Icons/ArrowRight.js'
import Mail from '@debbie/iris/dist/components/Icons/Mail.js'
import Phone from '@debbie/iris/dist/components/Icons/Phone.js'
import { File, LineThrough } from '@debbie/iris/dist/components/index.js'
import PaymentPortal from '@debbie/iris/dist/components/Pay/PaymentOption/PaymentPortal.js'
import moment from 'moment'
import React from 'react'
import { useSelector } from 'react-redux'
import { styled } from 'styled-components'

import { DATE_FORMAT_SHORT, GRID_SPACING } from '../theme.js'

const Info = (props: {
  caseVouchers: CaseVoucher[]
  singleCase: Case
  me: User
}) => {
  const { caseVouchers, singleCase, me } = props

  const creditorLoadable = useCreditor(singleCase?.creditorId)
  const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null

  const paymentPlansLoadable = usePaymentPlansByCaseId(singleCase?.caseId)
  const paymentPlans = h.hasData(paymentPlansLoadable)
    ? paymentPlansLoadable.data
    : null

  const collectorLoadable = useCollector(
    (singleCase &&
      creditor &&
      getCollectorOnCase(
        singleCase.type,
        singleCase.collectorId,
        creditor.identityCollectorId,
        creditor.defaultCollectorId,
      )) ||
      undefined,
  )
  const collector = h.hasData(collectorLoadable) ? collectorLoadable.data : null

  const lang = useSelector((s: RootState) => s.lang)

  const paymentPlan = _paymentPlans.getCurrentPaymentPlan(paymentPlans)

  const paymentOptions: PaymentOption[] =
    collector &&
    collector.paymentOptions &&
    singleCase &&
    singleCase.currency &&
    _collectors.getPaymentOptions(collector.paymentOptions, singleCase.currency)

  const paymentPortalOption =
    paymentOptions &&
    paymentOptions
      .filter((paymentOption) => paymentOption.hideOnDebtorweb !== true)
      .find((paymentOption) => paymentOption.isActive)

  return (
    <>
      <LineThrough>
        <span>{lang.debtorWebPaymentPlans.info}</span>
      </LineThrough>
      {paymentPlan && (
        <InfoArea>
          <h2>{lang.debtorWebPaymentPlans.paymentPlan}</h2>
          <p>
            {moment(paymentPlan.plan[0]?.date).format(DATE_FORMAT_SHORT)}{' '}
            <ArrowRight />{' '}
            {moment(paymentPlan.plan[paymentPlan.plan.length - 1]?.date).format(
              DATE_FORMAT_SHORT,
            )}
          </p>
          <p>
            {paymentPlan.plan.length} {lang.debtorWebPaymentPlans.months}
          </p>
          <p>
            {formatNumber(_paymentPlans.getAmount.entry(paymentPlan.plan) ?? 0)}{' '}
            {singleCase && singleCase.currency}/
            {lang.debtorWebPaymentPlans.entry}
          </p>
          {_paymentPlans.getAmount.upfront(paymentPlan.plan) && (
            <p>
              {lang.debtorWebPaymentPlans.firstPayment}:{' '}
              {singleCase && singleCase.currency}{' '}
              {formatNumber(
                _paymentPlans.getAmount.upfront(paymentPlan.plan) ?? 0,
              )}
            </p>
          )}
          {_paymentPlans.getAmount.rest(paymentPlan.plan) && (
            <p>
              {lang.debtorWebPaymentPlans.lastPayment}:{' '}
              {singleCase && singleCase.currency}{' '}
              {formatNumber(
                _paymentPlans.getAmount.rest(paymentPlan.plan) ?? 0,
              )}
            </p>
          )}
        </InfoArea>
      )}
      {paymentPlan && paymentPlan.documentFileId && (
        <InfoArea>
          <h2>{lang.debtorWebPaymentPlans.signedDoc}</h2>
          <p>
            <File
              file={{
                fileId: paymentPlan.documentFileId,
                contentType: 'application/pdf',
                fileName: 'skylderklæring.pdf',
              }}
            />
          </p>
        </InfoArea>
      )}
      {collector &&
        (validatePhone(collector.phone) || collector.email != null) && (
          <InfoArea>
            <h2>{lang.debtorWebPaymentPlans.needHelp}</h2>
            {collector && (
              <div>
                {collector.email != null && (
                  <p
                    style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    title={collector.email}
                  >
                    <Mail />{' '}
                    <a
                      style={{
                        color: 'inherit',
                      }}
                      href={`mailto:${collector.email}`}
                    >
                      {collector.email}
                    </a>
                  </p>
                )}
                {validatePhone(collector.phone) && (
                  <p
                    style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    title={`${collector.phone.locale} ${collector.phone.number}`}
                  >
                    <Phone />{' '}
                    <a
                      style={{
                        color: 'inherit',
                      }}
                      href={`tel:${collector.phone.locale}${collector.phone.number}`}
                    >
                      +{collector.phone.locale} {collector.phone.number}
                    </a>
                  </p>
                )}
              </div>
            )}
          </InfoArea>
        )}
      {singleCase && paymentPortalOption && (
        <InfoArea>
          <h2>{lang.debtorWebPaymentPlans.orPay}</h2>
          <PaymentPortal
            caseVouchers={caseVouchers}
            collector={collector}
            paymentOption={paymentPortalOption}
            case={singleCase}
          />
        </InfoArea>
      )}
    </>
  )
}

export default Info

const InfoArea = styled.div`
  font-size: 1.1em;
  margin-top: ${GRID_SPACING * 1.5}px;

  h2 {
    font-size: 0.8em;
    opacity: 0.8;
  }

  p {
    margin: 0.4em 0;
    font-size: 0.8em;
  }

  input[type='checkbox'] {
    font-size: 20px;
  }
`
